import type { CSSProperties } from 'react';
import type { ThemeContextValue } from '@course-authoring/shared-types';
import { createContentBlockStyles } from '@course-authoring/ui-blocks';

type ContentBlockStyles = ReturnType<typeof createContentBlockStyles>;
type EditorTokens = ThemeContextValue['tokens'];

export interface ComparisonSliderEditorStyles {
  container: CSSProperties;
  header: CSSProperties;
  titleRow: CSSProperties;
  titleLevelButtonGroup: CSSProperties;
  titleLevelButton: CSSProperties;
  titleLevelButtonActive: CSSProperties;
  titleEditor: CSSProperties;
  sectionLabel: CSSProperties;
  subtitleEditor: CSSProperties;
  contentTypeRow: CSSProperties;
  contentTypeButton: CSSProperties;
  contentTypeButtonActive: CSSProperties;
  contentTypeButtonInactive: CSSProperties;
  imageSizeSection: CSSProperties;
  optionsLayout: CSSProperties;
  optionPanel: CSSProperties;
  optionHeader: CSSProperties;
  collapsedLabelArea: CSSProperties;
  collapsedLabelInput: CSSProperties;
  collapsedLabelHelper: CSSProperties;
  panelFieldLabel: CSSProperties;
  optionTitleEditor: CSSProperties;
  optionBodyEditor: CSSProperties;
  fieldLabel: CSSProperties;
  optionContentPanel: CSSProperties;
  optionTextColumn: CSSProperties;
  imageInput: CSSProperties;
  imagePreview: CSSProperties;
  imagePlaceholder: CSSProperties;
  imagePickerButton: CSSProperties;
  imagePickerOverlay: CSSProperties;
  removeImageButton: CSSProperties;
  videoPickerButton: CSSProperties;
  videoPlaceholder: CSSProperties;
  videoPreview: CSSProperties;
  videoOverlay: CSSProperties;
  videoMetadataInput: CSSProperties;
  videoMetadataTextarea: CSSProperties;
  videoCharacterCount: CSSProperties;
  divider: CSSProperties;
}

export const comparisonSliderEditorInnerStyles = `
  .cs-editor p {
    margin: 0;
  }
  .cs-editor p:not(:last-child) {
    margin-bottom: 0.5em;
  }
`;

export const comparisonSliderToolbarStyles: Record<string, CSSProperties> = {
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px',
    marginBottom: '8px',
    paddingBottom: '8px',
    borderBottom: '1px solid #9ca3af',
  },
  toolbarButton: {
    minWidth: '32px',
    height: '32px',
    padding: '0 8px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#6b7280',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  toolbarButtonActive: {
    backgroundColor: '#3b82f6',
    color: '#fff',
  },
};

export function createComparisonSliderEditorStyles(
  tokens: EditorTokens,
  baseStyles: ContentBlockStyles,
  isFocused: boolean
): ComparisonSliderEditorStyles {
  return {
    container: {
      ...baseStyles.comparisonContainer,
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: isFocused ? tokens.brand.primary : 'transparent',
      transition: 'border-color 150ms',
    },
    header: {
      ...baseStyles.comparisonHeader,
    },
    titleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.md,
      marginBottom: tokens.spacing.sm,
    },
    titleLevelButtonGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      flexShrink: 0,
    },
    titleLevelButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '4px 10px',
      fontSize: '13px',
      fontWeight: 600,
      fontFamily: tokens.typography.fontFamily,
      borderRadius: tokens.radius.md,
      cursor: 'pointer',
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.app,
      color: tokens.text.muted,
      transition: tokens.ui.transitionDefault,
      minWidth: '36px',
      minHeight: '32px',
    },
    titleLevelButtonActive: {
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.surfaceAlt,
      color: tokens.text.primary,
      fontWeight: 700,
    },
    titleEditor: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.h2.size,
      fontWeight: tokens.typography.h2.weight,
      color: tokens.text.primary,
      lineHeight: tokens.typography.h2.lineHeight,
    },
    sectionLabel: {
      fontSize: '11px',
      fontWeight: 700,
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.muted,
      letterSpacing: '0.05em',
      textTransform: 'uppercase' as const,
      marginBottom: tokens.spacing.sm,
    },
    subtitleEditor: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.muted,
      lineHeight: tokens.typography.body.lineHeight,
      marginTop: tokens.spacing.sm,
    },
    contentTypeRow: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      marginBottom: tokens.spacing.sm,
    },
    contentTypeButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '6px 12px',
      fontSize: '13px',
      fontWeight: 500,
      fontFamily: tokens.typography.fontFamily,
      borderRadius: tokens.radius.full,
      cursor: 'pointer',
      transition: tokens.ui.transitionDefault,
      minWidth: '80px',
      minHeight: '32px',
    },
    contentTypeButtonActive: {
      border: `1px solid ${tokens.ui.selectionBorder}`,
      backgroundColor: tokens.ui.selectionBorder,
      color: '#fff',
    },
    contentTypeButtonInactive: {
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.app,
      color: tokens.text.muted,
    },
    imageSizeSection: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      marginBottom: tokens.spacing.lg,
    },
    optionsLayout: {
      display: 'flex',
      flexDirection: 'row',
      gap: tokens.spacing.lg,
      marginTop: tokens.spacing.lg,
    },
    optionPanel: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      padding: tokens.spacing.lg,
      backgroundColor: tokens.backgrounds.app,
    },
    optionHeader: {
      fontSize: '13px',
      fontWeight: 600,
      fontFamily: tokens.typography.fontFamily,
      color: tokens.brand.primary,
      letterSpacing: '0.02em',
      textTransform: 'uppercase' as const,
      paddingBottom: tokens.spacing.sm,
      borderBottom: `1px solid ${tokens.borders.default}`,
    },
    collapsedLabelArea: {
      backgroundColor: tokens.backgrounds.surfaceAlt,
      borderRadius: tokens.radius.md,
      padding: tokens.spacing.md,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
    },
    collapsedLabelInput: {
      width: '100%',
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      fontSize: tokens.typography.body.size,
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.primary,
      backgroundColor: tokens.backgrounds.app,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      outline: 'none',
      boxSizing: 'border-box' as const,
    },
    collapsedLabelHelper: {
      fontSize: '12px',
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.muted,
      lineHeight: '1.4',
    },
    panelFieldLabel: {
      fontSize: '11px',
      fontWeight: 700,
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.muted,
      letterSpacing: '0.05em',
      textTransform: 'uppercase' as const,
      marginBottom: tokens.spacing.xs,
    },
    optionTitleEditor: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.primary,
      lineHeight: tokens.typography.body.lineHeight,
      margin: 0,
    },
    optionBodyEditor: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.primary,
      lineHeight: tokens.typography.body.lineHeight,
      marginTop: tokens.spacing.sm,
    },
    fieldLabel: {
      fontSize: '12px',
      fontWeight: 600,
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.muted,
      marginBottom: tokens.spacing.xs,
      marginTop: tokens.spacing.md,
    },
    optionContentPanel: {
      ...baseStyles.comparisonContentPanel,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.app,
    },
    optionTextColumn: {
      flex: 1,
      minWidth: 0,
    },
    imageInput: {
      width: '100%',
      maxWidth: '100%',
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      fontSize: tokens.typography.body.size,
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.primary,
      backgroundColor: tokens.backgrounds.app,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      outline: 'none',
      boxSizing: 'border-box' as const,
    },
    imagePreview: {
      maxWidth: '100%',
      maxHeight: '100%',
      borderRadius: tokens.radius.md,
      display: 'block',
    },
    imagePlaceholder: {
      width: '100%',
      height: '100%',
      backgroundColor: tokens.backgrounds.surfaceAlt,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: tokens.spacing.sm,
      color: tokens.brand.primary,
      fontSize: '13px',
      fontWeight: 500,
      fontFamily: tokens.typography.fontFamily,
    },
    imagePickerButton: {
      width: '100%',
      aspectRatio: '1 / 1',
      padding: 0,
      borderRadius: tokens.radius.md,
      overflow: 'hidden',
      border: `1px dashed ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.app,
      cursor: 'pointer',
      textAlign: 'left' as const,
      position: 'relative' as const,
    },
    imagePickerOverlay: {
      position: 'absolute' as const,
      left: 0,
      right: 0,
      bottom: 0,
      padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
      backgroundColor: 'rgba(0, 0, 0, 0.45)',
      color: tokens.text.onInverse,
      fontSize: '12px',
      fontWeight: 600,
      fontFamily: tokens.typography.fontFamily,
      textAlign: 'center' as const,
    },
    removeImageButton: {
      marginTop: tokens.spacing.sm,
      padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
      borderRadius: tokens.radius.full,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.app,
      color: tokens.text.muted,
      fontSize: '12px',
      fontWeight: 600,
      fontFamily: tokens.typography.fontFamily,
      cursor: 'pointer',
      minHeight: '32px',
    },
    videoPickerButton: {
      width: '100%',
      aspectRatio: '16 / 9',
      padding: 0,
      borderRadius: tokens.radius.md,
      overflow: 'hidden',
      border: `1px dashed ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.app,
      cursor: 'pointer',
      textAlign: 'left' as const,
      position: 'relative' as const,
    },
    videoPlaceholder: {
      width: '100%',
      height: '100%',
      backgroundColor: tokens.backgrounds.surfaceAlt,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: tokens.spacing.sm,
      color: tokens.brand.primary,
      fontSize: '13px',
      fontWeight: 500,
      fontFamily: tokens.typography.fontFamily,
    },
    videoPreview: {
      maxWidth: '100%',
      maxHeight: '100%',
      borderRadius: tokens.radius.md,
      display: 'block',
    },
    videoOverlay: {
      position: 'absolute' as const,
      left: 0,
      right: 0,
      bottom: 0,
      padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
      backgroundColor: 'rgba(0, 0, 0, 0.45)',
      color: tokens.text.onInverse,
      fontSize: '12px',
      fontWeight: 600,
      fontFamily: tokens.typography.fontFamily,
      textAlign: 'center' as const,
    },
    videoMetadataInput: {
      width: '100%',
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      fontSize: tokens.typography.body.size,
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.primary,
      backgroundColor: tokens.backgrounds.app,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      outline: 'none',
      boxSizing: 'border-box' as const,
    },
    videoMetadataTextarea: {
      width: '100%',
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      fontSize: tokens.typography.body.size,
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.primary,
      backgroundColor: tokens.backgrounds.app,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      outline: 'none',
      boxSizing: 'border-box' as const,
      resize: 'vertical' as const,
    },
    videoCharacterCount: {
      fontSize: '11px',
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.muted,
      textAlign: 'right' as const,
      marginTop: '2px',
    },
    divider: {
      width: '100%',
      height: '1px',
      backgroundColor: tokens.borders.default,
      margin: `${tokens.spacing.lg} 0`,
      border: 'none',
    },
  };
}
